import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const GoogleLocation = ({ onLocationSelect, initialLocation }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const defaultCenter = {
    lat: 17.3850, // Hyderabad coordinates
    lng: 78.4867
  };

  // Map click handler for Leaflet
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const location = {
          lat: e.latlng.lat,
          lng: e.latlng.lng
        };
        setSelectedLocation(location);
        if (onLocationSelect) {
          onLocationSelect(location);
        }
      },
    });
    return null;
  };

  useEffect(() => {
    // If initialLocation is provided, use it but don't call onLocationSelect back
    // to avoid potential infinite loops with parent state
    if (initialLocation && initialLocation.lat && initialLocation.lng) {
      const location = {
        lat: parseFloat(initialLocation.lat),
        lng: parseFloat(initialLocation.lng)
      };
      setCurrentLocation(location);
      setSelectedLocation(location);
      setIsLoading(false);
      return;
    }

    // Otherwise, get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(location);
          setSelectedLocation(location);
          setIsLoading(false);
          if (onLocationSelect) {
            onLocationSelect(location);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setCurrentLocation(defaultCenter);
          setSelectedLocation(defaultCenter);
          setIsLoading(false);
          if (onLocationSelect) {
            onLocationSelect(defaultCenter);
          }
        }
      );
    } else {
      setCurrentLocation(defaultCenter);
      setSelectedLocation(defaultCenter);
      setIsLoading(false);
      if (onLocationSelect) {
        onLocationSelect(defaultCenter);
      }
    }
    // We intentionally only run this on initial mount or if values are missing
    // or when we genuinely need to switch initial location
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation?.lat, initialLocation?.lng]);

  if (isLoading) {
    return (
      <div style={{
        ...mapContainerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div>Loading map...</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            Please wait while we initialize the map
          </div>
        </div>
      </div>
    );
  }

  return (
    <MapContainer
      center={[currentLocation?.lat || defaultCenter.lat, currentLocation?.lng || defaultCenter.lng]}
      zoom={15}
      style={mapContainerStyle}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapClickHandler />
      {selectedLocation && (
        <Marker
          position={[selectedLocation.lat, selectedLocation.lng]}
          draggable={true}
          eventHandlers={{
            dragend: (e) => {
              const location = {
                lat: e.target.getLatLng().lat,
                lng: e.target.getLatLng().lng
              };
              setSelectedLocation(location);
              if (onLocationSelect) {
                onLocationSelect(location);
              }
            }
          }}
        />
      )}
    </MapContainer>
  );
};

export default GoogleLocation;
